import { useEffect, useState } from 'react';
import { GearFill } from 'react-bootstrap-icons';
import { StaffPageHeader } from '../components/StaffPageHeader';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { FormFeedback } from '../components/FormFeedback';
import { useAccount } from '../Context/AccountProvider';
import { getAllSettings, updateSetting } from '../services/SettingsService';

// Human-readable metadata for known setting keys
const SETTING_META = {
  googleDriveDownloadLink: {
    label:       'Google Drive Download Link',
    description: 'Direct download URL shown on the Download page (Google Drive mirror).',
  },
  mediaFireDownloadLink: {
    label:       'MediaFire Download Link',
    description: 'Direct download URL shown on the Download page (MediaFire mirror).',
  },
  nobilityResetMessage: {
    label:       'Nobility Reset Message',
    description: 'Message displayed in-game during nobility resets.',
    multiline:   true,
  },
  nobilityResetDate: {
    label:       'Nobility Reset Date',
    description: 'Scheduled month for the next nobility reset.',
    placeholder: 'YYYY-MM',
    pattern:     '\\d{4}-\\d{2}',
  },
};

const HIDDEN_KEYS = new Set(['cp_per_coin']);

function formatKey(key) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, c => c.toUpperCase())
    .trim();
}

function SettingRow({ name, value, onChange, disabled }) {
  const meta = SETTING_META[name] || {};
  const label = meta.label || formatKey(name);
  const inputType = meta.type || 'text';

  return (
    <div className="sset-row">
      <div className="sset-row-label">
        <span className="sset-label">{label}</span>
        {meta.description && <span className="sset-desc">{meta.description}</span>}
        <span className="sset-key">{name}</span>
      </div>
      <div className="sset-row-input">
        {meta.multiline ? (
          <textarea
            className="sset-textarea"
            value={value}
            onChange={e => onChange(e.target.value)}
            disabled={disabled}
            rows={3}
          />
        ) : (
          <input
            className="sset-input"
            type={inputType}
            value={value}
            placeholder={meta.placeholder}
            pattern={meta.pattern}
            onChange={e => onChange(e.target.value)}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
}

export function StaffSettingsPage() {
  const { toast } = useAccount();

  const [settings, setSettings] = useState([]);
  const [values,   setValues]   = useState({});
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await getAllSettings();
        const filtered = (data.result || []).filter(s => !HIDDEN_KEYS.has(s.key));
        if (!cancelled) {
          setSettings(filtered);
          const init = {};
          filtered.forEach(s => { init[s.key] = s.value ?? ''; });
          setValues(init);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await Promise.all(settings.map(s => updateSetting(s.key, values[s.key] ?? '')));
      toast('success', 'Settings Saved', 'All site settings have been updated.');
    } catch (err) {
      setError(err.message || 'One or more settings could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="sset-page">
      <StaffPageHeader
        icon={<GearFill />}
        title="Site Settings"
        description="Manage global website configuration"
      />

      {error && <FormFeedback type="error" message={error} />}

      <div className="sset-card">
        {settings.length === 0 ? (
          <p className="sset-empty">No settings found.</p>
        ) : (
          settings.map((s, i) => (
            <SettingRow
              key={s.key}
              name={s.key}
              value={values[s.key] ?? ''}
              onChange={val => setValues(prev => ({ ...prev, [s.key]: val }))}
              disabled={saving}
            />
          ))
        )}
      </div>

      <div className="sset-footer">
        <button
          className="sset-save-btn"
          type="button"
          onClick={handleSave}
          disabled={saving || settings.length === 0}
        >
          {saving ? 'Saving…' : 'Save All Settings'}
        </button>
      </div>
    </div>
  );
}
